import { OrgFullDetail } from "@garther/interfaces";
import {
  useAcceptOrgInvitationMutation,
  useCreateOrgLocationMutation,
  useCreateOrgMutation,
  useCreateReviewMutation,
  useDeleteOrgLocationMutation,
  useRemoveOrgMemberMutation,
  useSendOrgInvitationMutation,
  useUpdateOrgMemberRoleMutation,
  useUpdateOrgMutation
} from "@garther/query-hooks";
import { getErrorMessage, toast, useI18n } from "@garther/ui/libs";
import { useRouter } from "@tanstack/react-router";

export {
  orgKeys,
  useOrgsByUser as useOrgsByUserQuery,
  usePopularOrgs as usePopularOrgsQuery,
  useOrgInvitationByToken,
  useOrgPendingInvitations as useOrgPendingInvitationsQuery
} from "@garther/query-hooks";

export function useCreateOrg(options: { onSuccess?: (result: OrgFullDetail) => void } = {}) {
  const { t } = useI18n();
  return useCreateOrgMutation({
    onSuccess: options.onSuccess,
    onError: (error) => toast.error(getErrorMessage(error) || t("common.error.tryAgain"))
  });
}

export function useUpdateOrg(options: { onSuccess?: () => void } = {}) {
  const { t } = useI18n();
  const router = useRouter();
  return useUpdateOrgMutation({
    onSuccess: () => {
      router.invalidate();
      options.onSuccess?.();
    },
    onError: (error) => toast.error(getErrorMessage(error) || t("common.error.tryAgain"))
  });
}

export function useUpdateOrgMemberRole(options: { onSuccess?: () => void } = {}) {
  const { t } = useI18n();
  const router = useRouter();
  return useUpdateOrgMemberRoleMutation({
    onSuccess: () => {
      router.invalidate();
      options.onSuccess?.();
    },
    onError: (error) => toast.error(getErrorMessage(error) || t("common.error.tryAgain"))
  });
}

export function useRemoveOrgMember(options: { onSuccess?: () => void } = {}) {
  const { t } = useI18n();
  const router = useRouter();
  return useRemoveOrgMemberMutation({
    onSuccess: () => {
      router.invalidate();
      options.onSuccess?.();
    },
    onError: (error) => toast.error(getErrorMessage(error) || t("common.error.tryAgain"))
  });
}

export function useCreateOrgReview(options: { onSuccess?: () => void } = {}) {
  const { t } = useI18n();
  return useCreateReviewMutation({
    onSuccess: options.onSuccess ? () => options.onSuccess!() : undefined,
    onError: (error) => toast.error(getErrorMessage(error) || t("common.error.tryAgain"))
  });
}

export function useCreateOrgLocation(options: { onSuccess?: () => void } = {}) {
  const { t } = useI18n();
  return useCreateOrgLocationMutation({
    onSuccess: options.onSuccess ? () => options.onSuccess!() : undefined,
    onError: (error) => toast.error(getErrorMessage(error) || t("common.error.tryAgain"))
  });
}

export function useDeleteOrgLocation(options: { onSuccess?: () => void } = {}) {
  const { t } = useI18n();
  return useDeleteOrgLocationMutation({
    onSuccess: options.onSuccess,
    onError: (error) => toast.error(getErrorMessage(error) || t("common.error.tryAgain"))
  });
}

export function useSendOrgInvitation(
  options: { onSuccess?: () => void; onError?: (error: unknown) => void } = {}
) {
  const { t } = useI18n();
  return useSendOrgInvitationMutation({
    onSuccess: options.onSuccess,
    onError: (error) => {
      toast.error(getErrorMessage(error) || t("common.error.tryAgain"));
      options.onError?.(error);
    }
  });
}

export function useAcceptOrgInvitation(options: { onSuccess?: () => void } = {}) {
  const { t } = useI18n();
  return useAcceptOrgInvitationMutation({
    onSuccess: options.onSuccess,
    onError: (error) => toast.error(getErrorMessage(error) || t("common.error.tryAgain"))
  });
}
